import React, { useState, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Switch } from './ui/switch';
import { Slider } from './ui/slider';
import { Label } from './ui/label';
import { 
  Upload, 
  FileSpreadsheet, 
  Loader2, 
  Download, 
  RefreshCw,
  AlertTriangle,
  CheckCircle,
  Users,
  Package,
  BarChart3,
  X,
  FileText,
  Settings,
  Filter,
  Search,
  Percent
} from 'lucide-react';
import { toast } from 'sonner';
import { authFetch } from '../lib/api';
import { cn } from '../lib/utils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')}/api`;

export const OrderMergeModal = ({ open, onClose, isBarbiePink }) => {
  const [masterFile, setMasterFile] = useState(null);
  const [orderFile, setOrderFile] = useState(null);
  const [masterData, setMasterData] = useState([]);
  const [orderData, setOrderData] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [uploadingMaster, setUploadingMaster] = useState(false);
  const [uploadingOrder, setUploadingOrder] = useState(false);
  
  // Processing Options (defaults: all enabled, threshold 75%)
  const [disablePreFiltering, setDisablePreFiltering] = useState(true);
  const [enableFuzzyMatching, setEnableFuzzyMatching] = useState(true);
  const [confidenceThreshold, setConfidenceThreshold] = useState(75);
  
  const masterInputRef = useRef(null);
  const orderInputRef = useRef(null);

  const handleFileDrop = useCallback(async (e, fileType) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (!file) return;

    const setUploading = fileType === 'master' ? setUploadingMaster : setUploadingOrder;
    const setFile = fileType === 'master' ? setMasterFile : setOrderFile;
    const setData = fileType === 'master' ? setMasterData : setOrderData;

    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);

      // Get auth token
      const token = localStorage.getItem('gs_session_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const response = await fetch(`${API}/order-merge/parse-file?file_type=${fileType}`, {
        method: 'POST',
        headers: headers,
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        setFile({ name: file.name, ...result });
        setData(result.data || []);
        toast.success(`${fileType === 'master' ? 'Master List' : 'Order List'} uploaded: ${result.row_count} rows`);
      } else {
        const err = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('File parse error:', err);
        toast.error(err.detail || 'Failed to parse file');
      }
    } catch (err) {
      console.error('File upload error:', err);
      toast.error(`Failed to upload file: ${err.message || 'Network error'}`);
    } finally {
      setUploading(false);
    }
  }, []);

  const processData = async () => {
    if (masterData.length === 0 || orderData.length === 0) {
      toast.error('Please upload both Master List and Order List');
      return;
    }

    setProcessing(true);
    try {
      const response = await authFetch(`${API}/order-merge/process`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          master_data: masterData,
          order_data: orderData,
          options: {
            disable_pre_filtering: disablePreFiltering,
            enable_fuzzy_matching: enableFuzzyMatching,
            confidence_threshold: confidenceThreshold
          }
        })
      });

      if (response.ok) {
        const result = await response.json();
        setResults(result);
        toast.success('Data processed successfully!');
      } else {
        const err = await response.json().catch(() => ({ detail: 'Unknown error' }));
        console.error('Order merge error:', err);
        toast.error(err.detail || 'Processing failed. Check console for details.');
      }
    } catch (err) {
      console.error('Order merge exception:', err);
      toast.error(`Processing failed: ${err.message || 'Network error'}`);
    } finally {
      setProcessing(false);
    }
  };

  const downloadCSV = () => {
    if (!results?.merged_data) return;

    const headers = ['First Name', 'Last Name', 'Team Name', 'Package Name', 'Quantity', 'Order Reference', 'Price', 'Confidence %'];
    const rows = results.merged_data.map(row => [
      row.first_name || '',
      row.last_name || '',
      row.team_name || '',
      row.package_name || '',
      row.quantity || 0,
      row.order_reference || '',
      row.price || 0,
      row.confidence || 0
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `OrderMerge_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    toast.success('CSV downloaded!');
  };

  const resetAll = () => {
    setMasterFile(null);
    setOrderFile(null);
    setMasterData([]);
    setOrderData([]);
    setResults(null);
    // Reset options to defaults
    setDisablePreFiltering(true);
    setEnableFuzzyMatching(true);
    setConfidenceThreshold(75);
  };

  const FileDropZone = ({ type, file, uploading, inputRef, onDrop }) => (
    <div
      className={cn(
        "border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer",
        file ? "border-green-500 bg-green-50 dark:bg-green-950/20" : "border-muted-foreground/25 hover:border-primary/50",
        uploading && "opacity-50 pointer-events-none"
      )}
      onDrop={(e) => onDrop(e, type)}
      onDragOver={(e) => e.preventDefault()}
      onClick={() => inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".xlsx,.xls,.csv,.txt,.docx"
        className="hidden"
        onChange={(e) => onDrop(e, type)}
      />
      
      {uploading ? (
        <div className="flex flex-col items-center gap-2">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Processing file...</p>
        </div>
      ) : file ? (
        <div className="flex flex-col items-center gap-2">
          <CheckCircle className="w-8 h-8 text-green-500" />
          <p className="font-medium">{file.name}</p>
          <p className="text-sm text-muted-foreground">{file.row_count} rows • {file.columns?.length || 0} columns</p>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <FileSpreadsheet className="w-8 h-8 text-muted-foreground" />
          <p className="font-medium">{type === 'master' ? 'Master Name List' : 'Order List'}</p>
          <p className="text-sm text-muted-foreground">
            Drop or click to upload
          </p>
          <p className="text-xs text-muted-foreground">
            .xlsx, .xls, .csv, .txt, .docx
          </p>
        </div>
      )}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Package className="w-6 h-6 text-primary" />
            OrderMerge Pro
          </DialogTitle>
          <DialogDescription>
            Harmonize Master Name Lists with Order Lists using AI-powered matching
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2" style={{ maxHeight: 'calc(90vh - 120px)' }}>
          <div className="space-y-6 pb-4">
            {/* File Upload Section */}
            {!results && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Master Name List (Members/Teams)
                  </h3>
                  <FileDropZone
                    type="master"
                    file={masterFile}
                    uploading={uploadingMaster}
                    inputRef={masterInputRef}
                    onDrop={handleFileDrop}
                  />
                </div>
                
                <div>
                  <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Order List (Packages/Payments)
                  </h3>
                  <FileDropZone
                    type="order"
                    file={orderFile}
                    uploading={uploadingOrder}
                    inputRef={orderInputRef}
                    onDrop={handleFileDrop}
                  />
                </div>
              </div>
            )}

            {/* Processing Options */}
            {!results && masterData.length > 0 && orderData.length > 0 && (
              <Card className="border-dashed">
                <CardHeader className="py-3 pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Processing Options
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 pb-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* Disable Pre-filtering */}
                    <div className="flex items-center justify-between space-x-3 p-3 rounded-lg bg-muted/50">
                      <div className="space-y-0.5">
                        <Label htmlFor="prefilter" className="text-sm font-medium flex items-center gap-2">
                          <Filter className="w-3.5 h-3.5" />
                          Disable Pre-filtering
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Analyze raw data directly
                        </p>
                      </div>
                      <Switch
                        id="prefilter"
                        checked={disablePreFiltering}
                        onCheckedChange={setDisablePreFiltering}
                      />
                    </div>

                    {/* Enable Fuzzy Matching */}
                    <div className="flex items-center justify-between space-x-3 p-3 rounded-lg bg-muted/50">
                      <div className="space-y-0.5">
                        <Label htmlFor="fuzzy" className="text-sm font-medium flex items-center gap-2">
                          <Search className="w-3.5 h-3.5" />
                          Enable Fuzzy Matching
                        </Label>
                        <p className="text-xs text-muted-foreground">
                          Match abbreviations & transposed names
                        </p>
                      </div>
                      <Switch
                        id="fuzzy"
                        checked={enableFuzzyMatching}
                        onCheckedChange={setEnableFuzzyMatching}
                      />
                    </div>

                    {/* Confidence Threshold */}
                    <div className="space-y-3 p-3 rounded-lg bg-muted/50">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium flex items-center gap-2">
                          <Percent className="w-3.5 h-3.5" />
                          Confidence Threshold
                        </Label>
                        <Badge variant="secondary" className="font-mono">
                          {confidenceThreshold}%
                        </Badge>
                      </div>
                      <Slider
                        value={[confidenceThreshold]}
                        onValueChange={(v) => setConfidenceThreshold(v[0])}
                        min={0}
                        max={100}
                        step={5}
                        className="w-full"
                      />
                      <p className="text-xs text-muted-foreground">
                        Only show matches above this confidence level
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Process Button */}
            {!results && masterData.length > 0 && orderData.length > 0 && (
              <div className="flex justify-center">
                <Button
                  size="lg"
                  onClick={processData}
                  disabled={processing}
                  className={cn("px-8", isBarbiePink && "bg-pink-500 hover:bg-pink-600")}
                >
                  {processing ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing with AI...
                    </>
                  ) : (
                    <>
                      <BarChart3 className="w-5 h-5 mr-2" />
                      Generate Report
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* Results Section */}
            {results && (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-2xl font-bold">{results.stats?.master_list_count || results.stats?.total_orders || 0}</div>
                      <p className="text-sm text-muted-foreground">Master List</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-2xl font-bold">{results.stats?.orders_count || results.stats?.total_quantity || 0}</div>
                      <p className="text-sm text-muted-foreground">Total Orders</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-2xl font-bold text-green-600">{results.stats?.matched_people || results.stats?.matched_count || 0}</div>
                      <p className="text-sm text-muted-foreground">Matched People</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-2xl font-bold text-amber-600">{results.stats?.unmatched_people || results.stats?.unmatched_count || 0}</div>
                      <p className="text-sm text-muted-foreground">No Orders</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-4">
                      <div className="text-2xl font-bold text-red-600">{results.stats?.unmatched_orders || results.unmatched_orders?.length || 0}</div>
                      <p className="text-sm text-muted-foreground">Unmatched Orders</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Revenue Summary */}
                {results.stats?.total_revenue > 0 && (
                  <Card className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 border-green-200">
                    <CardContent className="pt-4">
                      <div className="text-3xl font-bold text-green-700">${results.stats.total_revenue.toLocaleString()}</div>
                      <p className="text-sm text-green-600">Total Revenue</p>
                    </CardContent>
                  </Card>
                )}

                {/* Team Distribution Chart */}
                {results.team_distribution && results.team_distribution.length > 0 && (
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm flex items-center gap-2">
                        <BarChart3 className="w-4 h-4" />
                        Orders by Team
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart data={results.team_distribution.slice(0, 10)}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="team" tick={{ fontSize: 10 }} />
                            <YAxis />
                            <Tooltip />
                            <Bar dataKey="count" fill={isBarbiePink ? "#ec4899" : "#3b82f6"} />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Unmatched Orders Section */}
                {results.unmatched_orders && results.unmatched_orders.length > 0 && (
                  <Card className="border-red-500">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2 text-red-600">
                        <X className="w-4 h-4" />
                        Unmatched Orders ({results.unmatched_orders.length}) - Orders NOT found in Master List
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="max-h-48">
                        <Table>
                          <TableHeader className="sticky top-0 bg-background">
                            <TableRow>
                              <TableHead className="text-xs">Order #</TableHead>
                              <TableHead className="text-xs">Name on Order</TableHead>
                              <TableHead className="text-xs">Team</TableHead>
                              <TableHead className="text-xs">Package</TableHead>
                              <TableHead className="text-xs">Qty</TableHead>
                              <TableHead className="text-xs">Price</TableHead>
                              <TableHead className="text-xs">Reason</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {results.unmatched_orders.map((order, idx) => (
                              <TableRow key={idx} className="bg-red-50/50 dark:bg-red-950/20">
                                <TableCell className="text-xs font-mono">{order.order_ref || '-'}</TableCell>
                                <TableCell className="text-xs font-medium">{order.name_on_order || '-'}</TableCell>
                                <TableCell className="text-xs">{order.team_on_order || '-'}</TableCell>
                                <TableCell className="text-xs">{order.package || '-'}</TableCell>
                                <TableCell className="text-xs">{order.quantity || 1}</TableCell>
                                <TableCell className="text-xs">${order.price || 0}</TableCell>
                                <TableCell className="text-xs text-muted-foreground">{order.reason || 'Not found'}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                )}

                {/* Alerts Section */}
                {results.errors && results.errors.length > 0 && (
                  <Card className="border-amber-500">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2 text-amber-600">
                        <AlertTriangle className="w-4 h-4" />
                        Data Processing Alerts ({results.errors.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ScrollArea className="max-h-40">
                        <div className="space-y-2">
                          {results.errors.map((err, idx) => (
                            <div key={idx} className="text-sm p-2 bg-amber-50 dark:bg-amber-950/20 rounded">
                              <span className="font-medium">{err.name_searched || 'Unknown'}</span>
                              {err.package && <span> - {err.package}</span>}
                              {err.quantity && <span> (Qty: {err.quantity})</span>}
                              {err.message && <p className="text-xs text-muted-foreground mt-1">{err.message}</p>}
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    </CardContent>
                  </Card>
                )}

                {/* Preview Table */}
                {results.merged_data && results.merged_data.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Data Preview (First 100 rows)
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="border rounded-lg overflow-hidden">
                        <ScrollArea className="max-h-80">
                          <Table>
                            <TableHeader className="sticky top-0 bg-background">
                              <TableRow>
                                <TableHead className="text-xs">First Name</TableHead>
                                <TableHead className="text-xs">Last Name</TableHead>
                                <TableHead className="text-xs">Team</TableHead>
                                <TableHead className="text-xs">Package</TableHead>
                                <TableHead className="text-xs">Qty</TableHead>
                                <TableHead className="text-xs">Order Ref</TableHead>
                                <TableHead className="text-xs">Price</TableHead>
                                <TableHead className="text-xs">Conf.</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {results.merged_data.slice(0, 100).map((row, idx) => (
                                <TableRow key={idx}>
                                  <TableCell className="text-xs">{row.first_name || '-'}</TableCell>
                                  <TableCell className="text-xs">{row.last_name || '-'}</TableCell>
                                  <TableCell className="text-xs">{row.team_name || '-'}</TableCell>
                                  <TableCell className="text-xs">{row.package_name || '-'}</TableCell>
                                  <TableCell className="text-xs">{row.quantity || 0}</TableCell>
                                  <TableCell className="text-xs font-mono">{row.order_reference || '-'}</TableCell>
                                  <TableCell className="text-xs">${row.price || 0}</TableCell>
                                  <TableCell className="text-xs">
                                    <Badge 
                                      variant={row.confidence >= 80 ? "default" : row.confidence >= 50 ? "secondary" : "outline"}
                                      className={cn(
                                        "text-[10px] px-1",
                                        row.confidence >= 80 && "bg-green-500/20 text-green-700 border-green-500/30",
                                        row.confidence >= 50 && row.confidence < 80 && "bg-yellow-500/20 text-yellow-700 border-yellow-500/30",
                                        row.confidence < 50 && "bg-red-500/20 text-red-700 border-red-500/30"
                                      )}
                                    >
                                      {row.confidence || 0}%
                                    </Badge>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </ScrollArea>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Action Buttons */}
                <div className="flex justify-center gap-4">
                  <Button variant="outline" onClick={resetAll}>
                    <RefreshCw className="w-4 h-4 mr-2" />
                    New Import
                  </Button>
                  <Button onClick={downloadCSV} className={cn(isBarbiePink && "bg-pink-500 hover:bg-pink-600")}>
                    <Download className="w-4 h-4 mr-2" />
                    Download CSV
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrderMergeModal;
